import React from "react";
import { Link } from "react-router-dom";
import { Award } from "lucide-react";
import RoboticsShell from "./RoboticsShell";

/**
 * For Parents — warm, plain-language explanation of what the curriculum
 * asks of a family: pedagogy, safety, kit costs, time, Girls@TEC.
 */
export default function RoboticsForParents() {
  return (
    <RoboticsShell soft>
      <section className="rbx-page" data-testid="rbx-page-parents">
        <span className="rbx-eyebrow" style={{ color: "var(--rbx-coral)" }}>Section · For parents</span>
        <h1 className="rbx-page__title" data-testid="rbx-parents-title">
          Warm answers to the questions you&rsquo;d actually ask.
        </h1>
        <p className="rbx-page__lede">
          No jargon. No pitch deck. Six honest paragraphs about what your
          child would be doing.
        </p>

        <div className="rbx-qual-teaser" data-testid="rbx-parents-qual-teaser">
          <div className="rbx-qual-teaser__icon" aria-hidden="true"><Award size={22}/></div>
          <div className="rbx-qual-teaser__body">
            <h2 className="rbx-qual-teaser__title" data-testid="rbx-parents-qual-title">
              Recognised qualification pathway
            </h2>
            <p className="rbx-qual-teaser__copy">
              Every FutureTec lesson is designed to align with the world&rsquo;s
              leading engineering and STEM qualifications &mdash; from CREST
              Awards to BTEC (Pearson) to the Arduino Fundamentals
              Certification. Your child can earn a FutureTec certificate
              <em> and</em> prepare for external exams simultaneously.
            </p>
            <Link
              to="/robotics/qualifications"
              className="rbx-btn rbx-btn--primary rbx-btn--sm"
              data-testid="rbx-parents-qual-cta"
            >
              See the alignment &amp; download PDFs &rarr;
            </Link>
          </div>
        </div>

        <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {SECTIONS.map((s) => (
            <article key={s.q} className="rbx-card" data-testid={`rbx-parents-${s.slug}`}>
              <div className="rbx-band__num" style={{ color: "var(--rbx-coral)" }}>{s.tag}</div>
              <h3 style={{ fontFamily: "var(--rbx-font-display)", fontSize: 20, fontWeight: 600, margin: "6px 0 8px" }}>
                {s.q}
              </h3>
              <p style={{ color: "var(--rbx-ink), color: var(--rbx-ink-mute)", lineHeight: 1.6, fontSize: 14.5 }}>
                {s.a}
              </p>
            </article>
          ))}
        </div>
      </section>
    </RoboticsShell>
  );
}

const SECTIONS = [
  { slug: "pedagogy", tag: "01 · APPROACH",
    q: "What will my child actually learn?",
    a: "We teach the physical thing before the digital thing. A ten-year-old will hold an LED, feel a resistor, wire a circuit, and only then be allowed to write code for it. No black boxes. No copy-paste projects. If your child can't explain what's inside a chip, they haven't finished the lesson." },
  { slug: "safety", tag: "02 · SAFETY",
    q: "Is it safe?",
    a: "Ages 8–11 use only breadboards and the Micro:bit — no soldering, no voltages above 5 V. From age 12 we introduce a soldering iron with strict adult supervision guidelines and eye protection. From age 15, small bench power supplies with current-limit training. Every term begins with a safety refresher. No exceptions." },
  { slug: "cost", tag: "03 · KITS",
    q: "How much do the kits cost?",
    a: "Optional. Explorer starter ≈ US $25, Builder ≈ US $45, Engineer ≈ US $75. If your school runs the programme, kits are shared and your child needs nothing at home. Sponsorship available for families where cost is a barrier — apply through the Girls@TEC scholarship page (open to any student)." },
  { slug: "time", tag: "04 · TIME",
    q: "How much time does it take?",
    a: "Two lessons per week, each 20 to 40 minutes depending on the band, plus a small at-home tinker. A capstone weekend at the end of each term. Ten weeks per term, three terms per year. Roughly 90 minutes of your child's week — less than one football practice." },
  { slug: "girls", tag: "05 · GIRLS@TEC",
    q: "What is Girls@TEC?",
    a: "A structural commitment, not a marketing page. We noticed girls dropping out of maths and technical subjects because of social pressure — especially in Asia. Girls@TEC is our scholarship + role-model + community strand designed to keep them in. Any student can apply, but our review prioritises girls first." },
  { slug: "why", tag: "06 · WHY US",
    q: "Why FutureTec?",
    a: "The team behind this organised the first LEGO Robotics Olympiad in Sri Lanka in 2004 — before \"STEM\" was a word people used. We had the privilege of working near Sir Arthur C. Clarke during his last decade in Colombo, and we're carrying that torch. This is not a hobby-kit reseller writing curriculum for the first time." },
];
